/* eslint-disable no-magic-numbers */

import { groupBy } from 'lodash';
import { ChartOfAccountsAccount } from '@futrli-sdk/predict';
import {
  ASSUMPTION_TYPES_URL_PARAMS,
  ASSUMPTION_TYPES,
  PAYMENT_TYPES_URL_PARAMS,
  PAYMENT_TYPES
} from '../../../core/constants';
import {
  PaymentTreatment,
  MultiPaymentTreatment,
  PredictionType,
  TOTALS_CODE,
  profitAndLossAccountsInOrder,
  accountCategoriesInOrder,
  AccountCategory
} from '../types';
import { AllowedPaymentTypes } from '../../detail/reports/actions/actions';

/**
 * Takes a URL formula type parameter and converts it to a PredictionType used by the UI
 * @param urlType the URL prediction type parameter
 */
export const convertUrlType = (urlType?: string) => {
  switch (urlType) {
    case ASSUMPTION_TYPES_URL_PARAMS[ASSUMPTION_TYPES.BASIC]: {
      return PredictionType.FREESTYLE;
    }
    case ASSUMPTION_TYPES_URL_PARAMS[ASSUMPTION_TYPES.FORMULA]: {
      return PredictionType.FORMULA;
    }
    case ASSUMPTION_TYPES_URL_PARAMS[ASSUMPTION_TYPES.REPEATING]: {
      return PredictionType.REPEATING;
    }
    case ASSUMPTION_TYPES_URL_PARAMS[ASSUMPTION_TYPES.UNITS]: {
      return PredictionType.UNIT;
    }
    default: {
      return PredictionType.FREESTYLE;
    }
  }
};

/**
 * Grouping accounts by category and sorting them in specific order
 * @param accounts all fetched accounts
 * @returns
 */
export const getParsedAccounts = (
  accounts: ChartOfAccountsAccount[],
  showOnlyProfitAndLoss = false,
  accountCategoryNames: Record<AccountCategory, string>
) => {
  const groupedAccounts = groupBy(accounts, 'category');

  const sortedAccounts: ChartOfAccountsAccount[] = [];
  const accountsCategoriesToDisplay = showOnlyProfitAndLoss ? profitAndLossAccountsInOrder : accountCategoriesInOrder;

  accountsCategoriesToDisplay.forEach(accountCategory => {
    const accountsInGroup = groupedAccounts[accountCategory];
    if (accountsInGroup) {
      sortedAccounts.push({
        code: TOTALS_CODE,
        name: accountCategoryNames[accountCategory],
        category: accountCategory
      } as ChartOfAccountsAccount);
      sortedAccounts.push(...accountsInGroup.sort((a, b) => a.name.localeCompare(b.name)));
    }
  });

  return sortedAccounts;
};

/**
 * Returns payment data in the format needed for making API requests
 * @param paymentTreatment
 * @param paymentTerms
 * @param multiPaymentData
 * @param nonCashAccountId
 */
export const getPaymentData = (
  paymentTreatment: PaymentTreatment,
  paymentTerms: string,
  multiPaymentData: MultiPaymentTreatment[],
  nonCashAccountId: string
) => {
  switch (paymentTreatment) {
    case PaymentTreatment.SAME_DAY: {
      return { payment_terms: 0 };
    }
    case PaymentTreatment.SINGLE: {
      return { payment_terms: Number(paymentTerms) };
    }
    case PaymentTreatment.MULTI_PAYMENT_DAYS: {
      return {
        payments: multiPaymentData
          .filter(d => d.payment_percentage && d.payment_terms)
          .map(d => ({
            payment_terms: Number(d.payment_terms),
            payment_percentage: d.payment_percentage!
          }))
      };
    }
    default: {
      return { account_id: nonCashAccountId };
    }
  }
};

/**
 * Returns the payment data to save based on the given payment type
 * @param paymentDataParams - The payment data params obtained from the UI
 * @param paymentType - The chosen payment type
 */
export const getPaymentDataSaveParams = (
  paymentDataParams: Partial<AllowedPaymentTypes>,
  paymentType: PAYMENT_TYPES
) => {
  const paymentTypeKey = PAYMENT_TYPES_URL_PARAMS[paymentType];
  const paymentTypeKeyToUse = paymentTypeKey === 'non-cash' ? 'non_cash' : paymentTypeKey;

  return {
    [paymentTypeKeyToUse]: paymentDataParams
  };
};

/**
 * Helper for getting the percentage sum of the multi-payment parts
 */
export const getPercentageSum = (multiPaymentData: MultiPaymentTreatment[]) => {
  const percentageSum = multiPaymentData.reduce((acc, curr) => {
    if (typeof curr.payment_percentage === 'string') {
      return acc + Number(curr.payment_percentage);
    }
    return acc;
  }, 0);

  return percentageSum;
};

/**
 * Converts a predictionType to the equivalent string used in URLs / API requests
 * @param predictionType
 */
export const getPredictionTypeArg = (predictionType: PredictionType) => {
  switch (predictionType) {
    case PredictionType.FORMULA:
      return ASSUMPTION_TYPES.FORMULA;
    case PredictionType.FREESTYLE:
      return ASSUMPTION_TYPES.BASIC;
    case PredictionType.REPEATING:
      return ASSUMPTION_TYPES.REPEATING;
    default:
      return ASSUMPTION_TYPES.UNITS;
  }
};

/**
 * Converts an assumption type string used in URLs / API requests to a PredictionType
 * @param assumptionType
 */
export const getPredictionType = (assumptionType: ASSUMPTION_TYPES) => {
  switch (assumptionType) {
    case ASSUMPTION_TYPES.FORMULA:
      return PredictionType.FORMULA;
    case ASSUMPTION_TYPES.BASIC:
      return PredictionType.FREESTYLE;
    case ASSUMPTION_TYPES.REPEATING:
      return PredictionType.REPEATING;
    case ASSUMPTION_TYPES.UNITS:
      return PredictionType.UNIT;
    default:
      return undefined;
  }
};

/**
 * Helper for determining if cashflow treatment for multi payment in days is disabled
 * (missing some fields or have percentage sum different than 100)
 * @param multiPaymentData current multi payment data
 * @param percentageSum current percentage sum of all payments
 */
export const isDisabledMultiPayment = (multiPaymentData: MultiPaymentTreatment[], percentageSum: number) => {
  for (const d of multiPaymentData) {
    if ((d.payment_percentage && !d.payment_terms) || (!d.payment_percentage && d.payment_terms)) {
      return true;
    }
  }

  return percentageSum !== 100;
};

/**
 * Returns true or false based upon if the given payment details are valid or not
 */
export const isCashFlowTreatmentDisabled = (
  paymentTreatment: PaymentTreatment,
  paymentTerms: string,
  multiPaymentData: MultiPaymentTreatment[],
  nonCashAccountIndex: string,
  accountIndex: string
) => {
  return (
    (paymentTreatment === PaymentTreatment.SINGLE && !paymentTerms) ||
    (paymentTreatment === PaymentTreatment.MULTI_PAYMENT_DAYS &&
      isDisabledMultiPayment(multiPaymentData, getPercentageSum(multiPaymentData))) ||
    (paymentTreatment === PaymentTreatment.NON_CASH_TRANSFER && !nonCashAccountIndex) ||
    (paymentTreatment === PaymentTreatment.NON_CASH_TRANSFER && nonCashAccountIndex === accountIndex)
  );
};

export const getLinesFirstInvalidCellIndex = (
  impactedLines: { name: string; data: string[]; valid?: boolean[] }[]
): [Record<string, boolean>, number] => {
  const newInvalidLines: Record<string, boolean> = {};
  let firstInvalidLine = -1;
  impactedLines?.forEach(line => {
    const lineInvalid = (line.valid || []).findIndex(v => !v);
    newInvalidLines[line.name] = lineInvalid !== -1;
    if (lineInvalid !== -1 && firstInvalidLine === -1) {
      firstInvalidLine = lineInvalid;
    }
  });
  return [newInvalidLines, firstInvalidLine];
};
